import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRuoloUtente } from "@/hooks/use-ruolo-utente";
import { PERSONALI, arrivoDi } from "@/lib/accessi";
import { LogOut, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/* Il ruolo, come testo sotto il nome. La mappa e gli stati in cui il ruolo NON si conosce stanno in
   `use-ruolo-utente`: qui si disegna soltanto, e un ruolo che non è arrivato non si inventa. */
function Ruolo({ className }) {
  const { stato, ruolo } = useRuoloUtente();
  if (stato === "assente") return null;
  if (stato === "attesa")
    return (
      <span data-testid="user-role-badge" data-stato-ruolo="attesa" className={cn("block h-3 w-20 animate-ida-skeleton rounded-xs bg-white/25", className)}>
        <span className="sr-only">Ruolo in caricamento</span>
      </span>
    );
  return (
    <span data-testid="user-role-badge" data-stato-ruolo={stato === "noto" ? "noto" : "ignoto"} className={cn("block truncate text-caption font-normal", className)}>
      {stato === "noto" ? ruolo.label : "Ruolo non disponibile"}
    </span>
  );
}

/* La persona in fondo al menu blu (computer e menu del telefono) oppure, `compatto`, solo l'iniziale nella
   testata blu. Il menu che si apre è lo stesso: «Il tuo accesso» (profilo e codice di verifica, di ogni persona,
   lib/accessi.js) ed Esci. */
export default function UserMenu({ compatto = false }) {
  const { profile, session, signOut } = useAuth();
  /* L'email della sessione c'è anche quando `/me` non risponde: senza, il nome resterebbe vuoto. */
  const email = profile?.email || session?.user?.email || null;
  const initials = (profile?.nome?.[0] || email?.[0] || "?").toUpperCase();
  const nomeCompleto = profile?.nome ? `${profile.nome} ${profile.cognome || ""}`.trim() : null;
  const avatar = (
    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-nav-button font-bold text-nav-button-ink" aria-hidden="true">
      {initials}
    </span>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compatto ? (
          <button type="button" data-testid="user-menu-trigger" className="inline-flex size-11 items-center justify-center rounded-md" aria-label="Il tuo account">
            {avatar}
          </button>
        ) : (
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label="Il tuo account"
            className="flex w-full items-center gap-2.5 rounded-md bg-white/5 px-3 py-2.5 text-left text-white transition-colors duration-150 ease-enter hover:bg-white/10"
          >
            {avatar}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{nomeCompleto || email || "Utente"}</span>
              <Ruolo className="text-white/75" />
            </span>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={compatto ? "end" : "start"} side={compatto ? "bottom" : "top"} className="ida-motion min-w-60 shadow-overlay">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate font-semibold text-ink-strong">{nomeCompleto || email || "Utente"}</div>
          {nomeCompleto && email ? <div className="truncate text-small text-ink-2">{email}</div> : null}
          {compatto ? <Ruolo className="text-ink-2" /> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* «Il tuo accesso» è di ogni persona (lib/accessi.js, PERSONALI): il venditore attiva qui il suo
            codice di verifica, senza passare dalle Impostazioni. */}
        {arrivoDi(profile?.ruolo) ? (
          <DropdownMenuItem asChild data-testid="user-menu-sicurezza" className="min-h-10 cursor-pointer">
            <Link to={PERSONALI.sicurezza}>
              <ShieldCheck className="mr-2 size-[18px]" strokeWidth={1.75} />
              Il tuo accesso
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem data-testid="user-menu-signout" onClick={signOut} className="min-h-10 cursor-pointer text-danger-ink focus:text-danger-ink">
          <LogOut className="mr-2 size-[18px]" strokeWidth={1.75} />
          Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
